/* eslint-disable react/display-name */
import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import {
  getRestaurantProfile,
  restaurantStops,
  restaurantTrucks,
  createRestaurantStop,
  updateRestaurantStop,
  deleteRestaurantStop,
  checkOutOfStop,
  pauseRestaurantStop,
  resumeRestaurantStop,
  skipStopDate,
  unskipStopDate,
  duplicateRestaurantStop,
  createRestaurantTruck,
  updateRestaurantTruck,
  deleteRestaurantTruck,
  saveStopMenu,
} from '../apollo'
import {
  Box, Button, IconButton, Paper, Stack, Typography, Chip,
  Snackbar, Alert, CircularProgress, Dialog, DialogTitle, Tooltip,
  DialogContent, DialogActions, TextField, Select, MenuItem, List, ListItemButton,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import PlaceIcon from '@mui/icons-material/Place'
import LogoutIcon from '@mui/icons-material/Logout'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import ConfigurableValues from '../config/constants'
import { searchPlaces } from '../utils/mapbox'
import { truckStopsTheme, TS, focusRing } from './truckStopsTheme'
import StopMenuSection, { useStopMenuEditor, PillButton } from './TruckStopMenu'

const GET_PROFILE = gql`${getRestaurantProfile}`
const GET_STOPS = gql`${restaurantStops}`
const GET_TRUCKS = gql`${restaurantTrucks}`
const CREATE_STOP = gql`${createRestaurantStop}`
const UPDATE_STOP = gql`${updateRestaurantStop}`
const DELETE_STOP = gql`${deleteRestaurantStop}`
const CHECK_OUT = gql`${checkOutOfStop}`
const PAUSE_STOP = gql`${pauseRestaurantStop}`
const RESUME_STOP = gql`${resumeRestaurantStop}`
const SKIP_DATE = gql`${skipStopDate}`
const UNSKIP_DATE = gql`${unskipStopDate}`
const DUPLICATE_STOP = gql`${duplicateRestaurantStop}`
const CREATE_TRUCK = gql`${createRestaurantTruck}`
const UPDATE_TRUCK = gql`${updateRestaurantTruck}`
const DELETE_TRUCK = gql`${deleteRestaurantTruck}`
const SAVE_STOP_MENU = gql`${saveStopMenu}`

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const DAY_FULL = {
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
}
// Date.getDay() numbering.
const DAY_INDEX = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 }

// A monthly stop runs on one week of each month: 1st to 4th, or 5 = the last
// one (every month has a last Friday; only some have a fifth).
const WEEK_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: 'Last' },
]
const WEEK_WORD = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: 'last' }

const isRepeating = s => s.recurrence === 'WEEKLY' || s.recurrence === 'MONTHLY'

// Local YYYY-MM-DD.
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// The Nth (5 = last) given weekday of a month. month may run past 11.
const nthWeekday = (year, month, dayIdx, n) => {
  if (n === 5) {
    const d = new Date(year, month + 1, 0)
    while (d.getDay() !== dayIdx) d.setDate(d.getDate() - 1)
    return d
  }
  const d = new Date(year, month, 1)
  while (d.getDay() !== dayIdx) d.setDate(d.getDate() + 1)
  d.setDate(d.getDate() + (n - 1) * 7)
  return d
}

// Stops are stored as minutes from local midnight (a truck's schedule is
// recurring wall-clock), but people enter times as "HH:mm".
const strToMinutes = (s) => {
  if (!s) return null
  const [h, m] = String(s).split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}
const minutesToStr = (mins) => {
  const m = Number(mins) || 0
  // A window running past midnight is stored as > 1439; wrap it for display.
  const wrapped = m % 1440
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
}

const EMPTY_FORM = {
  _id: null,
  truckId: '',
  label: '',
  address: '',
  imageUrl: '',
  latitude: null,
  longitude: null,
  recurrence: 'ONE_OFF',
  stopDate: new Date().toISOString().slice(0, 10),
  dayOfWeek: 'MON',
  weekOfMonth: 1,
  exceptionDates: [],
  recurrenceStartDate: '',
  recurrenceEndDate: '',
  start: '11:00',
  end: '14:00',
  overnight: false,
  notes: '',
}

// ---- Presentation ----
const PAGE = {
  bgcolor: TS.page, color: TS.text, fontFamily: TS.font, fontSize: 15, lineHeight: 1.45,
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }
}
const CONTENT = {
  maxWidth: 1100, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 }
}
const SECTION_TITLE = { fontSize: 17, fontWeight: 700, color: TS.text, lineHeight: 1.35, mb: 1.5 }
const LABEL = { fontSize: 13, fontWeight: 600, color: TS.label, lineHeight: 1.4, mb: '6px' }
const TILE = { display: 'grid', placeItems: 'center', flexShrink: 0 }
const STOP_LIST = { display: 'flex', flexDirection: 'column', gap: 1.5 }
const CHIP = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', px: '10px', py: '5px',
  borderRadius: '8px', bgcolor: TS.chipBg, color: TS.label, fontSize: 14, fontWeight: 600, lineHeight: 1.3
}
const CHIP_ICON = { fontSize: 16, color: TS.secondary }
const STATUS_CHIP = { height: 24, borderRadius: '6px', fontSize: 12.5, fontWeight: 600, '& .MuiChip-label': { px: 1 } }
const STATUS = {
  parked: { ...STATUS_CHIP, bgcolor: TS.text, color: '#FFFFFF' },
  neutral: { ...STATUS_CHIP, bgcolor: TS.tileBg, color: TS.icon },
  amber: { ...STATUS_CHIP, bgcolor: TS.amberSoft, color: TS.amberText }
}
const ACTION_BTN = {
  width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
  bgcolor: TS.card, border: `1px solid ${TS.btnBorder}`, color: TS.icon,
  '&:hover': { bgcolor: TS.page },
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const DANGER_BTN = {
  ...ACTION_BTN,
  bgcolor: TS.dangerBg, borderColor: TS.dangerBorder, color: TS.danger,
  '&:hover': { bgcolor: '#FDE4E4' }
}

// A dashed placeholder row for an empty section.
const EmptyRow = ({ icon: Icon, children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2, p: '20px 22px',
    borderRadius: '14px', border: `1px dashed ${TS.inputBorder}`
  }}>
    <Box aria-hidden sx={{ ...TILE, width: 40, height: 40, borderRadius: '10px', bgcolor: TS.tileBg, color: TS.secondary }}>
      <Icon sx={{ fontSize: 22 }} />
    </Box>
    <Typography sx={{ color: TS.secondary, fontSize: 15 }}>{children}</Typography>
  </Box>
)

const TruckStops = () => {
  const { MAPBOX_TOKEN, SERVER_URL } = ConfigurableValues()
  const restaurantId = localStorage.getItem('restaurantId')

  const { data: profileData } = useQuery(GET_PROFILE, { variables: { id: restaurantId } })
  const restaurant = profileData?.restaurant
  const isMobile = restaurant?.isMobile === true

  const { data, loading, refetch } = useQuery(GET_STOPS, {
    variables: { restaurantId, includePast: true },
    fetchPolicy: 'network-only',
    skip: !restaurantId,
  })

  // The restaurant's trucks. Only a food truck has any (the query refuses
  // anything else), and it always has at least "Truck 1".
  const { data: trucksData, refetch: refetchTrucks } = useQuery(GET_TRUCKS, {
    variables: { restaurantId },
    fetchPolicy: 'network-only',
    skip: !restaurantId || !isMobile,
  })
  const trucks = useMemo(() => trucksData?.restaurantTrucks || [], [trucksData])
  const multiTruck = trucks.length > 1
  const truckNames = useMemo(() => new Map(trucks.map(t => [String(t._id), t.name])), [trucks])

  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [toast, setToast] = useState(null)

  const [predictions, setPredictions] = useState([])
  const [showPredictions, setShowPredictions] = useState(false)
  const searchTimer = useRef(null)

  // Which truck's stops are listed: 'ALL' or a truck id.
  const [truckFilter, setTruckFilter] = useState('ALL')
  // Add / rename a truck inline: { mode: 'add' | 'rename', id, name }.
  const [truckEdit, setTruckEdit] = useState(null)

  const [uploading, setUploading] = useState(false)
  const photoInput = useRef(null)
  const [menuExpanded, setMenuExpanded] = useState(false)
  // Only a saved stop has a menu to load; nothing loads while the dialog is shut.
  const menuEditor = useStopMenuEditor(open && form._id ? form._id : null)

  const done = (message, severity = 'success') => {
    setToast({ message, severity })
    refetch()
  }
  const failed = (err) => setToast({
    message: err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong',
    severity: 'error',
  })

  // Errors from these three are caught in submit(), which runs them in order.
  const [createStop, { loading: creating }] = useMutation(CREATE_STOP)
  const [updateStop, { loading: updating }] = useMutation(UPDATE_STOP)
  const [saveMenu, { loading: savingMenu }] = useMutation(SAVE_STOP_MENU)

  const truckDone = (message) => {
    setTruckEdit(null)
    setToast({ message, severity: 'success' })
    refetchTrucks()
    refetch()
  }
  const [createTruck, { loading: creatingTruck }] = useMutation(CREATE_TRUCK, {
    onCompleted: (res) => {
      truckDone('Truck added')
      if (res?.createRestaurantTruck?._id) setTruckFilter(String(res.createRestaurantTruck._id))
    },
    onError: failed,
  })
  const [updateTruck, { loading: renamingTruck }] = useMutation(UPDATE_TRUCK, {
    onCompleted: () => truckDone('Truck renamed'),
    onError: failed,
  })
  const [deleteTruck] = useMutation(DELETE_TRUCK, {
    onCompleted: () => { setTruckFilter('ALL'); truckDone('Truck removed') },
    onError: failed,
  })
  const [deleteStop] = useMutation(DELETE_STOP, {
    onCompleted: () => done('Stop removed'),
    onError: failed,
  })
  const [checkOut] = useMutation(CHECK_OUT, {
    onCompleted: () => done('Truck checked out'),
    onError: failed,
  })
  const [pauseStop] = useMutation(PAUSE_STOP, { onCompleted: () => done('Stop paused'), onError: failed })
  const [resumeStop] = useMutation(RESUME_STOP, { onCompleted: () => done('Stop is back on'), onError: failed })
  const [skipDate] = useMutation(SKIP_DATE, { onCompleted: () => done('Skipping that date'), onError: failed })
  const [unskipDate] = useMutation(UNSKIP_DATE, { onCompleted: () => done('Back on for that date'), onError: failed })
  const [duplicateStop] = useMutation(DUPLICATE_STOP, { onCompleted: () => done('Copied to the new date'), onError: failed })

  const stops = useMemo(() => data?.restaurantStops || [], [data])
  const today = new Date().toISOString().slice(0, 10)

  // A truck that was just removed can't stay selected.
  const filterId = truckFilter !== 'ALL' && truckNames.has(String(truckFilter)) ? String(truckFilter) : 'ALL'
  const firstTruckId = trucks[0] ? String(trucks[0]._id) : null
  // A stop with no truck yet belongs to the first one (the backend homes it there).
  const truckOf = s => (s.truckId != null ? String(s.truckId) : firstTruckId)
  const shown = filterId === 'ALL' ? stops : stops.filter(s => truckOf(s) === filterId)
  const upcomingFor = id => stops.filter(s => (id === 'ALL' || truckOf(s) === id) &&
    (isRepeating(s) || (s.stopDate || '') >= today)).length

  const weekly = shown.filter(s => s.recurrence === 'WEEKLY')
  const monthly = shown.filter(s => s.recurrence === 'MONTHLY')
  // One-offs split by whether they have happened yet: upcoming is a plan, past
  // is a library to copy from when the same event comes round again.
  const oneOff = shown.filter(s => !isRepeating(s) && (s.stopDate || '') >= today)
  const pastEvents = shown
    .filter(s => !isRepeating(s) && (s.stopDate || '') < today && !s.isAdHoc)
    .sort((a, b) => (b.stopDate || '').localeCompare(a.stopDate || ''))
  const parkedNow = stops.find(s => s.isCheckedIn)

  // The next date a repeating stop lands on, so "skip next" needs no date picker.
  const nextOccurrence = (stop) => {
    if (stop.recurrence === 'MONTHLY') {
      const dayIdx = DAY_INDEX[stop.dayOfWeek]
      const n = Number(stop.weekOfMonth)
      if (dayIdx == null || !WEEK_WORD[n]) return null
      const localToday = ymd(new Date())
      const from = stop.recurrenceStartDate && stop.recurrenceStartDate > localToday ? stop.recurrenceStartDate : localToday
      const skipped = stop.exceptionDates || []
      const now = new Date()
      for (let i = 0; i < 24; i += 1) {
        const date = ymd(nthWeekday(now.getFullYear(), now.getMonth() + i, dayIdx, n))
        if (date >= from && !skipped.includes(date)) return date
      }
      return null
    }
    const target = DAYS.indexOf(stop.dayOfWeek)
    if (target < 0) return null
    const d = new Date()
    // DAYS is Monday-first; getDay() is Sunday-first.
    for (let i = 0; i < 8; i += 1) {
      const idx = (d.getDay() + 6) % 7
      if (idx === target) return d.toISOString().slice(0, 10)
      d.setDate(d.getDate() + 1)
    }
    return null
  }

  const onSkipNext = (stop) => {
    const date = nextOccurrence(stop)
    if (!date) return failed(new Error('Could not work out the next date'))
    skipDate({ variables: { id: stop._id, date } })
  }

  const onPause = (stop) => {
    const answer = window.prompt(
      'Pause until when? Enter a date as YYYY-MM-DD, or leave blank to pause until further notice.',
      ''
    )
    if (answer === null) return
    const until = answer.trim()
    if (until && !/^\d{4}-\d{2}-\d{2}$/.test(until)) {
      return failed(new Error('Use YYYY-MM-DD, or leave it blank'))
    }
    pauseStop({ variables: { id: stop._id, until: until || null, reason: null } })
  }

  const onUseAgain = (stop) => {
    const answer = window.prompt('What date is it on this time? (YYYY-MM-DD)', today)
    if (!answer) return
    const date = answer.trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return failed(new Error('Use YYYY-MM-DD'))
    duplicateStop({ variables: { id: stop._id, stopDate: date } })
  }

  // Same Mapbox autocomplete the rest of the panel uses — picking a suggestion is
  // what supplies coordinates. Typing a raw address still works: the backend
  // geocodes it on save.
  const onAddressChange = useCallback((value) => {
    setForm(f => ({ ...f, address: value, latitude: null, longitude: null }))
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (value.length > 2) {
      searchTimer.current = setTimeout(async () => {
        try {
          const results = await searchPlaces(value, MAPBOX_TOKEN, { limit: 5 })
          setPredictions(results)
          setShowPredictions(results.length > 0)
        } catch (_) { setShowPredictions(false) }
      }, 300)
    } else {
      setShowPredictions(false)
    }
  }, [MAPBOX_TOKEN])

  // A new stop goes on the truck being looked at, else the first truck.
  const openNew = () => {
    setForm({ ...EMPTY_FORM, truckId: filterId !== 'ALL' ? filterId : (firstTruckId || '') })
    setShowPredictions(false)
    setMenuExpanded(false)
    setOpen(true)
  }

  const openEdit = (s) => {
    setForm({
      _id: s._id,
      truckId: truckOf(s) || '',
      label: s.label || '',
      address: s.address || '',
      imageUrl: s.imageUrl || '',
      latitude: s.latitude,
      longitude: s.longitude,
      recurrence: s.recurrence,
      stopDate: s.stopDate || new Date().toISOString().slice(0, 10),
      dayOfWeek: s.dayOfWeek || 'MON',
      weekOfMonth: Number(s.weekOfMonth) || 1,
      exceptionDates: s.exceptionDates || [],
      recurrenceStartDate: s.recurrenceStartDate || '',
      recurrenceEndDate: s.recurrenceEndDate || '',
      start: minutesToStr(s.startMinute),
      end: minutesToStr(s.endMinute),
      overnight: Number(s.endMinute) > 1439,
      notes: s.notes || '',
    })
    setShowPredictions(false)
    setMenuExpanded(false)
    setOpen(true)
  }

  // Typing on a stop's menu can be a lot of work; don't lose it to a stray click.
  const closeDialog = () => {
    if (menuEditor.dirty && !window.confirm('Close without saving the changes to this stop’s menu?')) return
    setOpen(false)
  }

  // andSetMenu: a new stop is saved and the dialog stays open on its menu.
  const submit = async (andSetMenu = false) => {
    const startMinute = strToMinutes(form.start)
    let endMinute = strToMinutes(form.end)
    if (startMinute == null || endMinute == null) return failed(new Error('Enter a start and end time'))
    // "Runs past midnight" is expressed by pushing the end into the next day.
    if (form.overnight) endMinute += 1440
    if (endMinute <= startMinute) return failed(new Error('The stop must end after it starts — tick "runs past midnight" for an overnight window'))

    const repeatingForm = isRepeating(form)
    const stop = {
      // Left out on update = stays on its truck; on create = the first truck.
      truckId: form.truckId || null,
      label: form.label.trim() || null,
      address: form.address.trim(),
      // "" removes the photo.
      imageUrl: form.imageUrl || '',
      latitude: form.latitude,
      longitude: form.longitude,
      recurrence: form.recurrence,
      stopDate: form.recurrence === 'ONE_OFF' ? form.stopDate : null,
      dayOfWeek: repeatingForm ? form.dayOfWeek : null,
      weekOfMonth: form.recurrence === 'MONTHLY' ? Number(form.weekOfMonth) : null,
      // Optional season for a repeating stop: a farmers' market that only runs
      // May to October, rather than every week forever.
      recurrenceStartDate: repeatingForm ? (form.recurrenceStartDate || null) : null,
      recurrenceEndDate: repeatingForm ? (form.recurrenceEndDate || null) : null,
      // The update saves whatever is sent here, so send the skipped dates back
      // or editing a stop would un-skip them.
      ...(form._id && repeatingForm ? { exceptionDates: form.exceptionDates || [] } : {}),
      startMinute,
      endMinute,
      notes: form.notes.trim() || null,
    }

    // Check the menu before saving anything, so a typo doesn't half-save.
    let menuVars = null
    if (form._id && menuEditor.dirty) {
      const built = menuEditor.build()
      if (built.error) {
        setMenuExpanded(true)
        return failed(new Error(built.error))
      }
      menuVars = built.variables
    }

    try {
      if (form._id) {
        await updateStop({ variables: { id: form._id, stop } })
        if (menuVars) {
          const res = await saveMenu({ variables: menuVars })
          if (res?.data?.saveStopMenu) menuEditor.loaded(res.data.saveStopMenu)
        }
        setOpen(false)
        done(menuVars ? 'Stop and its menu saved' : 'Stop updated')
      } else {
        const res = await createStop({ variables: { restaurantId, stop } })
        const newId = res?.data?.createRestaurantStop?._id
        if (andSetMenu && newId) {
          setForm(f => ({ ...f, _id: newId }))
          setMenuExpanded(true)
          done('Stop added. Now choose what it sells here.')
        } else {
          setOpen(false)
          done('Stop added')
        }
      }
    } catch (err) {
      failed(err)
      refetch()
    }
  }

  // Same upload the Profile page uses: the backend stores the file and hands
  // back its URL, which is saved on the stop with everything else.
  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      const base = SERVER_URL.endsWith('/') ? SERVER_URL : `${SERVER_URL}/`
      const res = await fetch(`${base}upload/image`, { method: 'POST', body: formData })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.url) throw new Error(json.error || 'Upload failed')
      setForm(f => ({ ...f, imageUrl: json.url }))
    } catch (err) {
      failed(err)
    } finally {
      setUploading(false)
    }
  }

  // ---- Trucks ----
  const activeTruck = trucks.length === 1 ? trucks[0] : trucks.find(t => String(t._id) === filterId)

  const saveTruck = (e) => {
    e.preventDefault()
    const name = (truckEdit?.name || '').trim()
    if (!name) return
    if (truckEdit.mode === 'add') createTruck({ variables: { restaurantId, name } })
    else updateTruck({ variables: { id: truckEdit.id, name } })
  }

  const removeTruck = (t) => {
    if (!window.confirm(`Remove ${t.name}?`)) return
    deleteTruck({ variables: { id: t._id } })
  }

  if (!isMobile && restaurant) {
    return (
      <ThemeProvider theme={truckStopsTheme}>
        <Box sx={PAGE}>
          <Box sx={CONTENT}>
            <Paper elevation={0} sx={{
              p: { xs: 3, sm: 5 }, borderRadius: '16px', textAlign: 'center', maxWidth: 720, mx: 'auto',
              bgcolor: TS.card, border: `1px solid ${TS.border}`, boxShadow: 'none'
            }}>
              <Box aria-hidden sx={{ ...TILE, width: 52, height: 52, borderRadius: '14px', mx: 'auto', bgcolor: TS.amberSoft, color: TS.amberIcon }}>
                <PlaceIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.3, mt: 2, color: TS.text }}>
                {restaurant.name} is not a food truck
              </Typography>
              <Typography sx={{ color: TS.secondary, fontSize: 15, mt: 0.75 }}>
                Stops are for restaurants that move. Turn on “This is a food truck” in Profile to schedule them.
              </Typography>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  // "Every Tuesday", "2nd Saturday of the month", "Sat, Nov 21". One-off
  // labels leave the year out, and past events can be from last year.
  const scheduleText = (s) => {
    if (!s.scheduleLabel) return s.recurrence === 'WEEKLY' ? `Every ${DAY_FULL[s.dayOfWeek] || s.dayOfWeek}` : s.stopDate
    if (!isRepeating(s) && s.stopDate && s.stopDate.slice(0, 4) !== today.slice(0, 4)) {
      return `${s.scheduleLabel}, ${s.stopDate.slice(0, 4)}`
    }
    return s.scheduleLabel
  }

  const StopRow = ({ s, past = false }) => {
    const weeklyStop = s.recurrence === 'WEEKLY'
    const repeatingStop = isRepeating(s)
    const truckName = multiTruck ? truckNames.get(truckOf(s)) : null
    const skipped = (s.exceptionDates || [])
      .filter(d => d >= today)
      .sort()
    const nextSkip = skipped[0]

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: '18px', sm: '20px 22px' }, borderRadius: '14px', border: `1px solid ${TS.border}`, boxShadow: 'none',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2.5, rowGap: 2,
          bgcolor: s.isCheckedIn ? '#FFFBF2' : s.isPaused || past ? '#F8F9FB' : TS.card,
          borderColor: s.isCheckedIn ? '#F3D19A' : TS.border,
        }}
      >
        <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {s.imageUrl ? (
          <Box
            component="img" src={s.imageUrl} alt="" loading="lazy"
            sx={{
              width: 64, height: 64, borderRadius: '12px', objectFit: 'cover', flexShrink: 0,
              bgcolor: TS.tileBg, border: `1px solid ${TS.border}`
            }}
          />
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.35, color: TS.text, overflowWrap: 'anywhere' }}>
              {s.label || s.address}
            </Typography>
            {s.isCheckedIn && <Chip label="Parked here now" size="small" color="success" sx={STATUS.parked} />}
            {s.hasOwnMenu && <Chip label="Own menu" size="small" sx={STATUS.amber} />}
            {s.isPaused && (
              <Chip
                label={s.pausedUntil ? `Paused until ${s.pausedUntil}` : 'Paused'}
                size="small"
                sx={STATUS.neutral}
              />
            )}
            {!s.isPaused && !!nextSkip && (
              <Chip label={`Skipping ${nextSkip}`} size="small" color="warning" sx={STATUS.amber} />
            )}
            {s.isAdHoc && <Chip label="Unplanned" size="small" sx={STATUS.neutral} />}
            {s.latitude == null && <Chip label="No coordinates" size="small" color="warning" sx={STATUS.amber} />}
          </Stack>
          {/* Same facts as the old one-line summary, one chip or line each. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
            {truckName ? (
              <Box component="span" sx={CHIP}>
                <LocalShippingOutlinedIcon aria-hidden sx={CHIP_ICON} />
                {truckName}
              </Box>
            ) : null}
            <Box component="span" sx={CHIP}>
              <CalendarTodayOutlinedIcon aria-hidden sx={CHIP_ICON} />
              {scheduleText(s)}
            </Box>
            <Box component="span" sx={CHIP}>
              <ScheduleOutlinedIcon aria-hidden sx={CHIP_ICON} />
              {s.windowLabel}
            </Box>
            {repeatingStop && s.recurrenceEndDate ? (
              <Box component="span" sx={CHIP}>
                <EventOutlinedIcon aria-hidden sx={CHIP_ICON} />
                {`until ${s.recurrenceEndDate}`}
              </Box>
            ) : null}
          </Box>
          {s.label ? (
            <Typography sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 0.75, fontSize: 14, color: TS.secondary, overflowWrap: 'anywhere' }}>
              <PlaceOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
              {s.address}
            </Typography>
          ) : null}
        </Box>
        </Box>

        {/* Past events are a library to copy from, not a schedule to manage. */}
        {past ? (
          <Button
            size="small" startIcon={<ContentCopyIcon />} onClick={() => onUseAgain(s)}
          >
            Use again
          </Button>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {repeatingStop && !s.isPaused && (
              <Tooltip title={weeklyStop ? 'Not this week — skip the next date only' : 'Not this month — skip the next date only'}>
                <IconButton size="small" onClick={() => onSkipNext(s)} sx={ACTION_BTN}>
                  <EventBusyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!!nextSkip && !s.isPaused && (
              <Button
                size="small" onClick={() => unskipDate({ variables: { id: s._id, date: nextSkip } })}
              >
                Undo skip
              </Button>
            )}
            <Tooltip title={s.isPaused ? 'Start running this stop again' : 'Pause this stop without deleting it'}>
              <IconButton size="small" onClick={() => (s.isPaused ? resumeStop({ variables: { id: s._id } }) : onPause(s))} sx={ACTION_BTN}>
                {s.isPaused
                  ? <PlayCircleOutlineIcon fontSize="small" />
                  : <PauseCircleOutlineIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy to another date">
              <IconButton size="small" onClick={() => onUseAgain(s)} sx={ACTION_BTN}><ContentCopyIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(s)} sx={ACTION_BTN}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Remove for good">
              <IconButton size="small" onClick={() => deleteStop({ variables: { id: s._id } })} sx={DANGER_BTN}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Paper>
    )
  }

  return (
    <ThemeProvider theme={truckStopsTheme}>
      <Box sx={PAGE}>
        <Box sx={{ ...CONTENT, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box component="header" sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: TS.text }}>Truck Stops</Typography>
              <Typography sx={{ color: TS.secondary, fontSize: 15, mt: 0.75 }}>
                Where this truck parks. Customers see today’s stop as its address.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, height: 46, pl: 2, pr: 2.5 }}>
              Add stop
            </Button>
          </Box>

          {/* Where the truck is right now */}
          <Paper elevation={0} sx={{ p: { xs: '20px', sm: '24px 28px' }, borderRadius: '16px', bgcolor: TS.text, color: '#fff', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.5 }}>
              <Box aria-hidden sx={{ ...TILE, width: 52, height: 52, borderRadius: '14px', bgcolor: TS.amber, color: TS.text }}>
                <PlaceIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.3, color: '#fff', overflowWrap: 'anywhere' }}>
                  {restaurant?.serviceLocation?.isOpenNow
                    ? `Parked at ${restaurant.serviceLocation.label || restaurant.serviceLocation.address}`
                    : (restaurant?.serviceLocation?.nextStopLabel || 'No stop scheduled')}
                </Typography>
                <Typography sx={{ fontSize: 15, color: TS.bannerSub, mt: 0.5 }}>
                  {restaurant?.serviceLocation?.isOpenNow
                    ? `${restaurant.serviceLocation.windowLabel || ''}${parkedNow ? ' · checked in on the POS' : ''}`
                    : 'The truck reads as closed until its next stop starts.'}
                </Typography>
              </Box>
              {parkedNow && (
                <Button size="small" startIcon={<LogoutIcon />} onClick={() => checkOut({ variables: { restaurantId, stopId: parkedNow._id } })}
                  sx={{
                    color: '#fff', borderColor: 'rgba(255,255,255,0.45)', bgcolor: 'transparent', textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', borderColor: '#fff' }
                  }} variant="outlined">
                  Check out
                </Button>
              )}
            </Box>
          </Paper>

          {/* Trucks: several can run under one name. Picking one lists only its stops. */}
          {trucks.length > 0 && (
            <Paper component="section" aria-labelledby="trucks-heading" elevation={0} sx={{
              p: { xs: '18px', sm: '20px 22px' }, borderRadius: '14px', border: `1px solid ${TS.border}`,
              boxShadow: 'none', bgcolor: TS.card
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Typography id="trucks-heading" component="h2" sx={{ ...SECTION_TITLE, mb: 0.25 }}>Trucks</Typography>
                  <Typography sx={{ color: TS.secondary, fontSize: 14 }}>
                    {multiTruck
                      ? 'Each stop belongs to one truck. Pick a truck to see only its stops.'
                      : 'Running more than one truck? Add it here and give each stop its truck.'}
                  </Typography>
                </Box>
                {truckEdit?.mode !== 'add' && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => setTruckEdit({ mode: 'add', name: `Truck ${trucks.length + 1}` })}>
                    Add truck
                  </Button>
                )}
              </Box>

              <Box role="group" aria-label="Show stops for" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {multiTruck && (
                  <PillButton selected={filterId === 'ALL'} onClick={() => setTruckFilter('ALL')}>
                    All trucks
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8, fontWeight: 600 }}>{upcomingFor('ALL')}</Box>
                  </PillButton>
                )}
                {trucks.map(t => {
                  const id = String(t._id)
                  const selected = !multiTruck || filterId === id
                  return (
                    <PillButton key={id} selected={selected} onClick={() => setTruckFilter(id)}
                      sx={{ maxWidth: '100%', '& > span:first-of-type': { overflowWrap: 'anywhere', textAlign: 'left' } }}>
                      <LocalShippingOutlinedIcon aria-hidden sx={{ fontSize: 18 }} />
                      <Box component="span">{t.name}</Box>
                      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8, fontWeight: 600 }}>{upcomingFor(id)}</Box>
                    </PillButton>
                  )
                })}
              </Box>

              {truckEdit ? (
                <Box component="form" onSubmit={saveTruck} sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1.25, mt: 2 }}>
                  <TextField
                    label={truckEdit.mode === 'add' ? 'New truck name' : 'Truck name'}
                    placeholder="Truck 2" size="small" autoFocus
                    value={truckEdit.name}
                    onChange={e => setTruckEdit(te => ({ ...te, name: e.target.value }))}
                    inputProps={{ maxLength: 80 }}
                    sx={{ flex: '1 1 220px', minWidth: 0 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1.25 }}>
                    <Button type="submit" variant="contained" disabled={!truckEdit.name.trim() || creatingTruck || renamingTruck}>
                      {truckEdit.mode === 'add' ? 'Add truck' : 'Save name'}
                    </Button>
                    <Button onClick={() => setTruckEdit(null)}>Cancel</Button>
                  </Box>
                </Box>
              ) : activeTruck ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  <Button size="small" startIcon={<EditIcon />} aria-label={`Rename ${activeTruck.name}`}
                    onClick={() => setTruckEdit({ mode: 'rename', id: activeTruck._id, name: activeTruck.name })}>
                    Rename truck
                  </Button>
                  <Button size="small" startIcon={<DeleteOutlineIcon />} aria-label={`Remove ${activeTruck.name}`}
                    onClick={() => removeTruck(activeTruck)}
                    sx={{ bgcolor: TS.dangerBg, borderColor: TS.dangerBorder, color: TS.danger, '&:hover': { bgcolor: '#FDE4E4' } }}>
                    Remove truck
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ color: TS.secondary, fontSize: 13, mt: 1.5 }}>
                  Pick a truck to rename or remove it.
                </Typography>
              )}
            </Paper>
          )}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <>
              <Box component="section">
                <Typography component="h2" sx={SECTION_TITLE}>
                  Every week
                </Typography>
                {weekly.length === 0
                  ? <EmptyRow icon={RepeatRoundedIcon}>{monthly.length ? 'No weekly stops yet.' : 'No repeating stops yet.'}</EmptyRow>
                  : <Box sx={STOP_LIST}>{weekly.map(s => <StopRow key={s._id} s={s} />)}</Box>}
              </Box>

              {monthly.length > 0 && (
                <Box component="section">
                  <Typography component="h2" sx={SECTION_TITLE}>
                    Every month
                  </Typography>
                  <Box sx={STOP_LIST}>{monthly.map(s => <StopRow key={s._id} s={s} />)}</Box>
                </Box>
              )}

              <Box component="section">
                <Typography component="h2" sx={SECTION_TITLE}>
                  One-off stops
                </Typography>
                {oneOff.length === 0
                  ? <EmptyRow icon={EventOutlinedIcon}>Nothing scheduled.</EmptyRow>
                  : <Box sx={STOP_LIST}>{oneOff.map(s => <StopRow key={s._id} s={s} />)}</Box>}
              </Box>

              {pastEvents.length > 0 && (
                <Box component="section">
                  <Typography component="h2" sx={{ ...SECTION_TITLE, mb: 0.5 }}>
                    Past events
                  </Typography>
                  <Typography sx={{ color: TS.secondary, fontSize: 14, mb: 1.5 }}>
                    Kept so you can run the same one again without hunting for the address and hours.
                  </Typography>
                  <Box sx={STOP_LIST}>{pastEvents.map(s => <StopRow key={s._id} s={s} past />)}</Box>
                </Box>
              )}
            </>
          )}
        </Box>

        <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth
          sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(15, 23, 42, 0.62)' } }}
          PaperProps={{
            sx: {
              maxWidth: 640, borderRadius: '18px', boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
              // The dialog renders outside the page box, so it sets its own surface and font.
              bgcolor: '#FFFFFF', backgroundImage: 'none', color: TS.text, fontFamily: TS.font,
              m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' }
            }
          }}>
          <DialogTitle sx={{
            display: 'flex', alignItems: 'center', gap: 1.75, p: { xs: '24px 20px 0', sm: '28px 32px 0' },
            fontWeight: 800, fontSize: 24, lineHeight: 1.25, letterSpacing: '-0.01em', color: TS.text
          }}>
            <Box aria-hidden sx={{ ...TILE, width: 44, height: 44, borderRadius: '12px', bgcolor: TS.amberSoft, color: TS.amberIcon }}>
              <PlaceIcon sx={{ fontSize: 24 }} />
            </Box>
            {form._id ? 'Edit stop' : 'Add a stop'}
          </DialogTitle>
          <DialogContent sx={{ px: { xs: '20px', sm: '32px' }, pb: { xs: '24px', sm: '28px' }, '&&': { pt: { xs: '20px', sm: '24px' } } }}>
            <Stack spacing={2.25}>
              {multiTruck && (
                <Box>
                  <Typography id="stop-truck-label" sx={LABEL}>Truck</Typography>
                  <Select
                    size="small" fullWidth labelId="stop-truck-label"
                    value={form.truckId && truckNames.has(String(form.truckId)) ? String(form.truckId) : ''}
                    onChange={e => setForm(f => ({ ...f, truckId: e.target.value }))}
                  >
                    {trucks.map(t => <MenuItem key={t._id} value={String(t._id)}>{t.name}</MenuItem>)}
                  </Select>
                </Box>
              )}

              <TextField
                label="Name customers see (optional)"
                placeholder="Riverside Brewery"
                size="small"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                fullWidth
              />

              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Address"
                  size="small"
                  value={form.address}
                  onChange={e => onAddressChange(e.target.value)}
                  fullWidth
                  helperText={form.latitude != null
                    ? 'Position set from the suggestion you picked.'
                    : 'Pick a suggestion to set the exact spot, or type it and we will look it up on save.'}
                />
                {showPredictions && (
                  <Paper sx={{
                    position: 'absolute', zIndex: 10, width: '100%', mt: 0.5, maxHeight: 200, overflow: 'auto',
                    borderRadius: '10px', border: `1px solid ${TS.border}`, boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)'
                  }}>
                    <List dense>
                      {predictions.map((p, i) => (
                        <ListItemButton key={i} sx={{ minHeight: 44 }} onClick={() => {
                          setForm(f => ({ ...f, address: p.placeName, latitude: p.lat, longitude: p.lng }))
                          setShowPredictions(false)
                        }}>
                          <Typography sx={{ fontSize: 14 }}>{p.placeName}</Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              {/* Schedule */}
              <Box sx={{
                bgcolor: TS.page, borderRadius: '14px', p: '18px',
                display: 'grid', columnGap: 2, rowGap: 2.25,
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }
              }}>
                <Box>
                  <Typography sx={LABEL}>Repeats</Typography>
                  <Select
                    size="small" fullWidth value={form.recurrence}
                    onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}
                  >
                    <MenuItem value="ONE_OFF">Just once</MenuItem>
                    <MenuItem value="WEEKLY">Every week</MenuItem>
                    <MenuItem value="MONTHLY">Every month</MenuItem>
                  </Select>
                </Box>
                <Box>
                  {isRepeating(form) ? (
                    <>
                      <Typography sx={LABEL}>Day</Typography>
                      <Select
                        size="small" fullWidth value={form.dayOfWeek}
                        onChange={e => setForm(f => ({ ...f, dayOfWeek: e.target.value }))}
                      >
                        {DAYS.map(d => <MenuItem key={d} value={d}>{DAY_FULL[d]}</MenuItem>)}
                      </Select>
                    </>
                  ) : (
                    <>
                      <Typography sx={LABEL}>Date</Typography>
                      <TextField
                        type="date" size="small" fullWidth value={form.stopDate}
                        onChange={e => setForm(f => ({ ...f, stopDate: e.target.value }))}
                      />
                    </>
                  )}
                </Box>

                {form.recurrence === 'MONTHLY' && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography id="stop-week-label" sx={LABEL}>Which week of the month</Typography>
                    <Box role="group" aria-labelledby="stop-week-label" sx={{
                      display: 'grid', gap: '6px', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))'
                    }}>
                      {WEEK_OPTIONS.map(w => (
                        <PillButton
                          key={w.value}
                          selected={Number(form.weekOfMonth) === w.value}
                          onClick={() => setForm(f => ({ ...f, weekOfMonth: w.value }))}
                          sx={{ width: '100%', px: 0, borderRadius: '10px' }}
                        >
                          {w.label}
                        </PillButton>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: TS.secondary, mt: '6px' }}>
                      {`Runs on the ${WEEK_WORD[Number(form.weekOfMonth)] || '1st'} ${DAY_FULL[form.dayOfWeek] || ''} of every month.`}
                    </Typography>
                  </Box>
                )}

                {isRepeating(form) && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={LABEL}>
                      Season (optional) — leave blank to repeat forever
                    </Typography>
                    <Box sx={{ mt: 1, display: 'grid', columnGap: 2, rowGap: 2.25, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                      <TextField
                        type="date" size="small" fullWidth label={form.recurrence === 'MONTHLY' ? 'From' : 'First week'} InputLabelProps={{ shrink: true }}
                        value={form.recurrenceStartDate}
                        onChange={e => setForm(f => ({ ...f, recurrenceStartDate: e.target.value }))}
                      />
                      <TextField
                        type="date" size="small" fullWidth label={form.recurrence === 'MONTHLY' ? 'Until' : 'Last week'} InputLabelProps={{ shrink: true }}
                        value={form.recurrenceEndDate}
                        onChange={e => setForm(f => ({ ...f, recurrenceEndDate: e.target.value }))}
                      />
                    </Box>
                  </Box>
                )}

                <Box>
                  <Typography sx={LABEL}>Arrives</Typography>
                  <TextField type="time" size="small" fullWidth value={form.start}
                    onChange={e => setForm(f => ({ ...f, start: e.target.value }))} />
                </Box>
                <Box>
                  <Typography sx={LABEL}>Leaves</Typography>
                  <TextField type="time" size="small" fullWidth value={form.end}
                    onChange={e => setForm(f => ({ ...f, end: e.target.value }))} />
                </Box>

                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box component="label" sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 44,
                    fontSize: 15, lineHeight: 1.4, color: TS.text, cursor: 'pointer',
                    '& input': { width: 20, height: 20, m: 0, flexShrink: 0, accentColor: TS.text, cursor: 'pointer' },
                    '& input:focus-visible': focusRing
                  }}>
                    <input
                      type="checkbox"
                      checked={form.overnight}
                      onChange={e => setForm(f => ({ ...f, overnight: e.target.checked }))}
                    />
                    Runs past midnight (a late-night stop that ends the next morning)
                  </Box>
                </Box>
              </Box>

              {/* Photo of the spot, shown on this stop's box on the truck page */}
              <Box>
                <Typography id="stop-photo-label" sx={LABEL}>Photo of the spot (optional)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{
                    ...TILE, width: 96, height: 72, borderRadius: '12px', overflow: 'hidden',
                    bgcolor: TS.tileBg, color: TS.secondary, border: `1px solid ${TS.border}`
                  }}>
                    {form.imageUrl
                      ? <Box component="img" src={form.imageUrl} alt="This stop’s photo" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <PhotoCameraOutlinedIcon aria-hidden sx={{ fontSize: 26 }} />}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                    <Button
                      size="small" variant="outlined" disabled={uploading}
                      startIcon={uploading ? null : <AddPhotoAlternateOutlinedIcon />}
                      onClick={() => photoInput.current && photoInput.current.click()}
                      aria-describedby="stop-photo-label"
                    >
                      {uploading ? 'Uploading…' : (form.imageUrl ? 'Replace photo' : 'Upload photo')}
                    </Button>
                    {form.imageUrl && !uploading ? (
                      <Button size="small" onClick={() => setForm(f => ({ ...f, imageUrl: '' }))}>Remove photo</Button>
                    ) : null}
                    <input ref={photoInput} type="file" accept="image/*" hidden onChange={onPickPhoto} />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: TS.secondary, mt: '6px' }}>
                  Customers see it on this stop on the truck page. It saves with the stop.
                </Typography>
              </Box>

              <TextField
                label="Notes (staff only)" size="small" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth
              />

              <StopMenuSection
                editor={menuEditor}
                isNew={!form._id}
                canCreate={!!form.address.trim() && !uploading}
                creating={creating}
                onCreateAndOpen={() => submit(true)}
                expanded={menuExpanded}
                setExpanded={setMenuExpanded}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{
            px: { xs: '20px', sm: '32px' }, py: '18px', gap: '10px', flexWrap: 'wrap',
            borderTop: `1px solid ${TS.border}`,
            '& > :not(style) ~ :not(style)': { ml: 0 }
          }}>
            <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained" onClick={() => submit()}
              disabled={creating || updating || savingMenu || uploading || !form.address.trim()}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
            >
              {creating || updating || savingMenu ? <CircularProgress size={18} sx={{ color: TS.secondary }} /> : (form._id ? 'Save stop' : 'Add stop')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast?.severity || 'success'} onClose={() => setToast(null)}>
            {toast?.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  )
}

export default withTranslation()(TruckStops)
